import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, MessageSquare, XCircle } from 'lucide-react';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import { documentLabel } from '../../utils/documentTitle';
import { visibleCustomSpecs } from '../../utils/customSpecs';
import {
  getProposalBalanceDue,
  getProposalInvoicedAmount,
  isInvoicedLineItem,
} from '../../utils/proposalInvoicing';
import PublicStripePaymentModal from '../../components/Projects/Invoices/PublicStripePaymentModal';

function formatDimensions(dim) {
  if (!dim || typeof dim !== 'object') return '';
  const parts = [];
  if (dim.width) parts.push(`${dim.width}"W`);
  if (dim.length) parts.push(`${dim.length}"L`);
  if (dim.height) parts.push(`${dim.height}"H`);
  if (dim.depth) parts.push(`${dim.depth}"D`);
  return parts.join(' x ');
}

function formatColorMaterial(it) {
  return [it.color, it.finishes, it.materials].filter(Boolean).join(' / ');
}

export default function ClientProposalDetail() {
  const { projectId, proposalId } = useParams();
  const { clientAuth } = useClientAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [specConfirmed, setSpecConfirmed] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [submittingChanges, setSubmittingChanges] = useState(false);
  const [changesError, setChangesError] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineError, setDeclineError] = useState('');
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!clientAuth?.token) return;

    fetch(`${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        return data;
      })
      .then(setProposal)
      .catch((err) => setError(err.message || 'Failed to load proposal.'))
      .finally(() => setLoading(false));
  }, [clientAuth, projectId, proposalId]);

  async function handleApprove() {
    setApproveError('');
    setApproving(true);

    try {
      const res = await fetch(
        `${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientAuth.token}`,
          },
          credentials: 'include',
          body: JSON.stringify({ signatureName, specConfirmed }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        setApproveError(data?.error || 'Failed to approve. Please try again.');
        return;
      }

      setProposal((prev) => ({ ...prev, status: 'approved', approvedAt: data.approvedAt, approvedByName: signatureName }));
      setShowApproveModal(false);
    } catch {
      setApproveError('Something went wrong. Please try again.');
    } finally {
      setApproving(false);
    }
  }

  async function handleRequestChanges() {
    setChangesError('');
    setSubmittingChanges(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}/request-changes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${clientAuth.token}` },
          credentials: 'include',
          body: JSON.stringify({ note: changesNote }),
        }
      );
      const data = await res.json();
      if (!res.ok) { setChangesError(data?.error || 'Failed to submit. Please try again.'); return; }
      setProposal((prev) => ({ ...prev, status: 'changes_requested' }));
      setShowChangesModal(false);
      setChangesNote('');
    } catch {
      setChangesError('Something went wrong. Please try again.');
    } finally {
      setSubmittingChanges(false);
    }
  }

  async function handleDecline() {
    setDeclineError('');
    setDeclining(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}/decline`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${clientAuth.token}` },
          credentials: 'include',
          body: JSON.stringify({}),
        }
      );
      const data = await res.json();
      if (!res.ok) { setDeclineError(data?.error || 'Failed to decline. Please try again.'); return; }
      setProposal((prev) => ({ ...prev, status: 'declined' }));
      setShowDeclineModal(false);
    } catch {
      setDeclineError('Something went wrong. Please try again.');
    } finally {
      setDeclining(false);
    }
  }

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return <><Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link><p className="cp-error">{error}</p></>;
  if (!proposal) return null;

  const lineItems = Array.isArray(proposal.lineItems) ? proposal.lineItems : [];
  const isApproved = proposal.status === 'approved';
  const isDeclined = proposal.status === 'declined';
  const isChangesRequested = proposal.status === 'changes_requested';
  const isSettled = isApproved || isDeclined;
  const canApprove = clientAuth.permissions?.approveProposals !== false && !isSettled && !isChangesRequested;
  const proposalTotal = Number(proposal.totals?.grandTotal ?? proposal.totals?.total ?? 0);
  const proposalPaid = Number(proposal.totals?.paid || 0);
  // Items already moved onto an invoice are paid there, not on the proposal.
  const invoicedAmount = getProposalInvoicedAmount(proposal);
  const remainingBalance = getProposalBalanceDue(proposal);

  const tpl = proposal.proposalTemplate || {};
  const showDimensions = tpl.showDimensions !== false;
  const showColorMaterial = tpl.showColorMaterial !== false;
  const showCustomSpecs = tpl.showCustomSpecs !== false;
  const showProductLink = tpl.showProductLink === true;
  const showMsrpSavings = tpl.showMsrpSavings === true;
  const requireSpecConfirmation = tpl.requireSpecConfirmation === true;
  const requirePaymentOnApproval = tpl.requirePaymentOnApproval !== false;
  // With pay-on-approval turned off the studio bills through a separate
  // invoice, so the proposal never offers payment — not even once approved.
  const showPayNow = remainingBalance > 0 && requirePaymentOnApproval;

  return (
    <div className="cp-animate">
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>

      <div className="cp-card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              {documentLabel(proposal.proposalNumber, proposal.title) || `Proposal`}
            </h1>
            {proposal.createdAt && (
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Date: {new Date(proposal.createdAt).toLocaleDateString()}
              </p>
            )}
            {isApproved && proposal.approvedAt && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#065f46', display: 'flex', alignItems: 'center', gap: 5 }}>
                <CheckCircle2 size={13} />
                Approved {new Date(proposal.approvedAt).toLocaleDateString()}
                {proposal.approvedByName ? ` · ${proposal.approvedByName}` : ''}
              </p>
            )}
          </div>
          <ProposalStatusBadge status={proposal.status} />
        </div>

        {proposal.notes && (
          <div
            style={{ fontSize: 14, color: '#374151', marginBottom: 20, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: proposal.notes }}
          />
        )}

        {/* Line items */}
        {lineItems.length > 0 && (
          <table className="cp-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="center">Qty</th>
                <th className="right">Unit Price</th>
                <th className="right">Shipping</th>
                <th className="right">Tariff</th>
                <th className="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => {
                const selectedAlt = Array.isArray(item?.alternatives) && item.selectedAlternativeId
                  ? item.alternatives.find((a) => a.id === item.selectedAlternativeId)
                  : null;
                return (
                  <tr key={item?.id || i}>
                    <td>
                      <span style={{ fontWeight: 500 }}>{item?.description || '—'}</span>
                      {isInvoicedLineItem(item) && (
                        <span className="cp-badge cp-badge-green" style={{ marginLeft: 8 }}>Invoiced</span>
                      )}
                      {selectedAlt && (
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                          Selected: {selectedAlt.description}
                        </p>
                      )}
                      {showDimensions && formatDimensions(item?.dimensions) && (
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                          {formatDimensions(item.dimensions)}
                        </p>
                      )}
                      {showColorMaterial && formatColorMaterial(item || {}) && (
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                          {formatColorMaterial(item)}
                        </p>
                      )}
                      {showCustomSpecs &&
                        visibleCustomSpecs(item?.customSpecs).map((spec) => (
                          <p key={spec.id} style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                            {spec.label}{spec.value ? `: ${spec.value}` : ''}
                          </p>
                        ))}
                      {showProductLink && item?.link && (
                        <p style={{ margin: '2px 0 0', fontSize: 12 }}>
                          <a href={item.link} target="_blank" rel="noreferrer">View Product</a>
                        </p>
                      )}
                      {showMsrpSavings && Number(item?.msrp) > Number(item?.clientUnit) && (
                        <p style={{ margin: '2px 0 0', fontSize: 12 }}>
                          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                            MSRP: ${Number(item.msrp).toFixed(2)}
                          </span>
                          {' · '}
                          <span style={{ color: '#4a8a3a', fontWeight: 600 }}>
                            You save ${((Number(item.msrp) - Number(item.clientUnit)) * Number(item.qty || 1)).toFixed(2)}
                          </span>
                        </p>
                      )}
                    </td>
                    <td className="center">{item?.qty ?? '—'}</td>
                    <td className="right">${Number(item?.clientUnit || 0).toFixed(2)}</td>
                    <td className="right">{Number(item?.clientShipping) > 0 ? `$${Number(item.clientShipping).toFixed(2)}` : '—'}</td>
                    <td className="right">{Number(item?.clientTariff) > 0 ? `$${Number(item.clientTariff).toFixed(2)}` : '—'}</td>
                    <td className="right">${Number(item?.lineTotal || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Totals */}
        <div className="cp-totals" style={{ marginBottom: canApprove ? 28 : 0 }}>
          <div className="cp-totals-inner">
            <TotalRow label="Subtotal" value={proposal.totals?.subtotal} />
            {proposal.totals?.tax > 0 && <TotalRow label="Sales Tax" value={proposal.totals.tax} />}
            <TotalRow label="Total" value={proposalTotal} bold />
            {invoicedAmount > 0 && <TotalRow label="Invoiced" value={invoicedAmount} />}
            {proposalPaid > 0 && <TotalRow label="Paid" value={proposalPaid} />}
            {remainingBalance > 0 && <TotalRow label="Remaining Balance" value={remainingBalance} />}
          </div>
        </div>

        {tpl.termsText && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: canApprove ? 16 : 0 }}>
            <strong style={{ fontSize: 13 }}>Terms &amp; Conditions</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {tpl.termsText}
            </p>
          </div>
        )}

        {Array.isArray(proposal.attachments) && proposal.attachments.length > 0 && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: canApprove ? 16 : 0 }}>
            <strong style={{ fontSize: 13 }}>Attachments</strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {proposal.attachments.map((a) => (
                <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                  {a.name || 'Download attachment'}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Action bar */}
        {!isSettled && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            {!isChangesRequested && (
              <button className="cp-btn-ghost" onClick={() => setShowChangesModal(true)}>
                <MessageSquare size={15} />
                Request Changes
              </button>
            )}
            <button className="cp-btn-ghost" style={{ color: '#dc2626' }} onClick={() => setShowDeclineModal(true)}>
              <XCircle size={15} />
              Decline
            </button>
            {canApprove && (
              <button className="cp-btn-primary" onClick={() => setShowApproveModal(true)}>
                <CheckCircle2 size={15} />
                Approve Proposal
              </button>
            )}
          </div>
        )}

        {showPayNow && (
          <div style={{ borderTop: isSettled ? '1px solid #e5e7eb' : 'none', paddingTop: isSettled ? 22 : 0, marginTop: isSettled ? 0 : 14, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="cp-btn-primary" onClick={() => setShowPayment(true)}>
              Pay Now
            </button>
          </div>
        )}
      </div>

      {/* Request Changes modal */}
      {showChangesModal && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowChangesModal(false)}>
          <div className="cp-modal">
            <h2>Request Changes</h2>
            <p>Describe what you'd like changed. Your designer will be notified.</p>
            <label className="cp-label">Your message</label>
            <textarea
              className="cp-input"
              rows={4}
              value={changesNote}
              onChange={(e) => setChangesNote(e.target.value)}
              placeholder="What would you like updated?"
              autoFocus
              style={{ resize: 'vertical' }}
            />
            {changesError && <p className="cp-error" style={{ marginTop: 12 }}>{changesError}</p>}
            <div className="cp-modal-actions">
              <button className="cp-btn-ghost" onClick={() => setShowChangesModal(false)} disabled={submittingChanges}>Cancel</button>
              <button className="cp-btn-primary" onClick={handleRequestChanges} disabled={submittingChanges || !changesNote.trim()}>
                {submittingChanges ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline modal */}
      {showDeclineModal && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeclineModal(false)}>
          <div className="cp-modal">
            <h2>Decline Proposal</h2>
            <p>Are you sure you want to decline this proposal? Your designer will be notified.</p>
            {declineError && <p className="cp-error" style={{ marginTop: 12 }}>{declineError}</p>}
            <div className="cp-modal-actions">
              <button className="cp-btn-ghost" onClick={() => setShowDeclineModal(false)} disabled={declining}>Cancel</button>
              <button
                className="cp-btn-primary"
                onClick={handleDecline}
                disabled={declining}
                style={{ background: '#dc2626' }}
              >
                {declining ? 'Declining…' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve modal */}
      {showApproveModal && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowApproveModal(false)}>
          <div className="cp-modal">
            <h2>Approve Proposal</h2>
            <p>By approving, you confirm acceptance of this proposal and its terms.</p>

            <label className="cp-label">Type your full name to sign</label>
            <input
              className="cp-input"
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Your full name"
              autoFocus
            />

            {requireSpecConfirmation && (
              <label className="cp-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16, fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={specConfirmed}
                  onChange={(e) => setSpecConfirmed(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>I confirm the dimensions, color/material, and other item details shown in this proposal are correct.</span>
              </label>
            )}

            {approveError && <p className="cp-error" style={{ marginTop: 12 }}>{approveError}</p>}

            <div className="cp-modal-actions">
              <button
                className="cp-btn-ghost"
                onClick={() => setShowApproveModal(false)}
                disabled={approving}
              >
                Cancel
              </button>
              <button
                className="cp-btn-primary"
                onClick={handleApprove}
                disabled={approving || !signatureName.trim() || (requireSpecConfirmation && !specConfirmed)}
              >
                {approving ? 'Approving…' : 'Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {showPayment && (
        <PublicStripePaymentModal
          remainingBalance={remainingBalance}
          title="Pay Proposal"
          invoiceNumber={proposal.proposalNumber}
          createIntentEndpoint={`${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}/create-payment-intent`}
          applyPaymentEndpoint={`${API_BASE}/api/client/projects/${projectId}/proposals/${proposalId}/apply-payment`}
          headers={{ Authorization: `Bearer ${clientAuth.token}` }}
          metadata={{ proposalId, projectId }}
          onSuccess={(amount) => {
            setProposal((prev) => ({
              ...prev,
              totals: {
                ...prev.totals,
                paid: Number(((prev.totals?.paid ?? 0) + amount).toFixed(2)),
              },
            }));
            setShowPayment(false);
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}

function ProposalStatusBadge({ status }) {
  const map = {
    approved:          { cls: 'cp-badge-green',  label: 'Approved' },
    sent:              { cls: 'cp-badge-blue',   label: 'Sent' },
    draft:             { cls: 'cp-badge-gray',   label: 'Draft' },
    declined:          { cls: 'cp-badge-red',    label: 'Declined' },
    changes_requested: { cls: 'cp-badge-yellow', label: 'Changes Requested' },
  };
  const s = map[String(status || '').toLowerCase()] || map.draft;
  return <span className={`cp-badge ${s.cls}`}>{s.label}</span>;
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`cp-total-row${bold ? ' bold' : ''}`}>
      <span>{label}</span>
      <span>${Number(value || 0).toFixed(2)}</span>
    </div>
  );
}
